// 학부모 인증 Repository

import { API_BASE_URL } from '../../../config/api';
import {
  AuthResponse,
  KakaoLoginRequest,
  PhoneLinkRequest,
  PhoneLoginRequest,
  RefreshTokenRequest,
  LogoutRequest,
  SmsVerificationRequest,
  SmsVerificationResponse,
  SmsVerificationCheck,
  SmsVerificationResult,
} from '../types';

export class ParentAuthRepository {
  private baseURL = `${API_BASE_URL}/api/v1/parent/auth`;

  // 카카오 토큰 로그인 (JavaScript SDK 방식)
  async kakaoTokenLogin(request: {
    access_token: string;
    device_info?: Record<string, any>;
  }): Promise<{
    accessToken: string;
    refreshToken: string;
    parent: any;
    needs_phone_link: boolean;
  }> {
    const response = await fetch(`${this.baseURL}/kakao-token-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.detail || '카카오 로그인 실패');
    }

    const data = await response.json();

    // 백엔드 응답 형식 변환 (snake_case → camelCase)
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      parent: data.parent,
      needs_phone_link: data.needs_phone_link,
    };
  }

  // 카카오 로그인 (Legacy - OAuth Authorization Code 방식)
  async kakaoLogin(request: KakaoLoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${this.baseURL}/kakao/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '카카오 로그인 실패');
    }

    return response.json();
  }

  // 전화번호 연결
  async linkPhone(request: PhoneLinkRequest): Promise<void> {
    const response = await fetch(`${this.baseURL}/link-phone`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '전화번호 연결 실패');
    }
  }

  // SMS 인증번호 발송
  async sendSmsCode(request: SmsVerificationRequest): Promise<SmsVerificationResponse> {
    const response = await fetch(`${this.baseURL}/send-sms-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'SMS 발송 실패');
    }

    return response.json();
  }

  // SMS 인증번호 검증
  async verifySmsCode(request: SmsVerificationCheck): Promise<SmsVerificationResult> {
    const response = await fetch(`${this.baseURL}/verify-sms-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'SMS 인증 실패');
    }

    return response.json();
  }

  // 전화번호 로그인
  async phoneLogin(request: PhoneLoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${this.baseURL}/phone/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '로그인 실패');
    }

    const data = await response.json();
    
    // 백엔드 응답 형식 변환 (snake_case → camelCase)
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      parent: data.parent,
      isPhoneLinked: data.parent.phone ? true : false,
    };
  }

  // 토큰 갱신
  async refreshToken(request: RefreshTokenRequest): Promise<{ accessToken: string }> {
    const response = await fetch(`${this.baseURL}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '토큰 갱신 실패');
    }

    return response.json();
  }

  // 로그아웃
  async logout(request: LogoutRequest): Promise<void> {
    const response = await fetch(`${this.baseURL}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '로그아웃 실패');
    }
  }

  // 현재 사용자 정보 조회
  async getCurrentParent(accessToken: string): Promise<AuthResponse['parent']> {
    const response = await fetch(`${this.baseURL}/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '사용자 정보 조회 실패');
    }

    return response.json();
  }

  // 초대 링크 검증
  async verifyInvite(token: string): Promise<{
    valid: boolean;
    student_name: string;
    organization_name: string;
    phone: string;
    expires_at: string;
  }> {
    const response = await fetch(
      `${this.baseURL}/verify-invite?token=${encodeURIComponent(token)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '초대 링크 검증에 실패했습니다');
    }

    return response.json();
  }

  // 초대 링크로 회원가입 (SMS 인증 완료 후)
  async signupWithInvite(request: {
    token: string;
    name: string;
    phone: string;
    sms_code: string;
    password: string;
    device_info?: Record<string, any>;
  }): Promise<{
    access_token: string;
    refresh_token: string;
    parent: any;
    student_linked: boolean;
  }> {
    const response = await fetch(`${this.baseURL}/signup-with-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '회원가입에 실패했습니다');
    }

    return response.json();
  }
}

export default new ParentAuthRepository();
